import * as React from 'react';
import { Label } from '@fluentui/react';
import Text from 'src/components/text/Text';
import localeService from 'src/dataServices/LocaleService';
import { IRegistrationIdentifier } from 'src/state/nonprofit/types';
import { useRegistrationIdentifiers } from 'src/hooks/useRegistrationIdentifiers';

const LegalIdentifierLookupTextEnabledCountryCodes = ['US'];

export interface IProfileLegalIdentifierProps {
  registrationIdentifiers: IRegistrationIdentifier[];
  countryCode?: string;
  organizationType?: string;
}

const RegistrationIdentifiersView = ({
  registrationIdentifiers,
  countryCode,
  organizationType
}: IProfileLegalIdentifierProps): JSX.Element => {
  const extRegulatoryBodies = useRegistrationIdentifiers(countryCode || '', organizationType || '');

  const getRegulatoryBodyName = (ri: IRegistrationIdentifier): string => {
    if (!extRegulatoryBodies || extRegulatoryBodies.size === 0) {
      return ri.regulatoryBody;
    }

    // Try to find by identifierKey first
    if (ri.identifierKey) {
      const bodyByKey = extRegulatoryBodies.get(ri.identifierKey);
      if (bodyByKey?.name) {
        return bodyByKey.name;
      }
    }

    // Fallback: search by regulatoryBody (acronym)
    for (const body of extRegulatoryBodies.values()) {
      if (body.acronym === ri.identifierAcronym || body.textKey === ri.identifierKey) {
        return body.name;
      }
    }

    return ri.regulatoryBody;
  };

  return (
    <>
      {registrationIdentifiers && registrationIdentifiers.length > 0 ? (
        <>
          <div className="ms-Grid-row mt-3">
            <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl12">
              <p className="mb-3 font-weight-bold">
                {localeService.getText('ProfileV2', 'LegalIdentifierLookupLabel')}
              </p>

              {LegalIdentifierLookupTextEnabledCountryCodes.includes(countryCode || '') && (
                <p
                  dangerouslySetInnerHTML={{
                    __html: [localeService.getText('ProfileV2', 'LegalIdentifierLookupText')].join(' ')
                  }}
                ></p>
              )}
            </div>
          </div>
          {registrationIdentifiers.map((ri, index) => (
            <React.Fragment key={index}>
            <div className="ms-Grid-row mt-3">
              <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
                <div className="field">
                  <Label tabIndex={-1} id="RegulatoryBody">
                    <Text category="ProfileV2" id="RegistrationType" />
                  </Label>
                </div>
                <div className="value">
                  <span id={`regulatoryBody${index}`} aria-labelledby="RegulatoryBody">
                    <b>{getRegulatoryBodyName(ri)}</b>
                  </span>
                </div>
              </div>
              <div className="ms-Grid-col ms-sm12 ms-md4 ms-lg4 ms-xl4">
                <div className="field">
                  <Label tabIndex={-1} id={'field' + `legalIdentifier${index}`}>
                    <Text category="Profile" id="LegalIdentifier" />
                  </Label>
                </div>
                <div className="value">
                  <span id={`legalIdentifier${index}`} aria-labelledby="LegalIdentifier">
                    <b>{ri.legalIdentifier}</b>
                  </span>
                </div>
              </div>
            </div>
            </React.Fragment>
          ))}
        </>
      ) : (
        <div className="ms-Grid-row">
          <div className="ms-Grid-col ms-sm12 ms-md12 ms-lg12 ms-xl12">
            <Text category="Profile" id="NoRegistrationIdentifiers" />
          </div>
        </div>
      )}
    </>
  );
};

export default RegistrationIdentifiersView;
